import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const ACJointInjury = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'What is an AC joint separation?', answer: 'An AC (acromioclavicular) joint separation is an injury to the ligaments connecting the clavicle to the acromion (part of the scapula). It is different from a shoulder dislocation. Severity ranges from Grade I (sprain) to Grade VI (severe displacement).' },
    { question: 'Do AC joint injuries need surgery?', answer: 'Most AC joint injuries (Grade I-II, and many Grade III) heal well without surgery. Surgery is typically recommended for Grade IV-VI injuries, or Grade III injuries in heavy laborers, overhead athletes, or those with persistent symptoms after conservative treatment.' },
    { question: 'How long is recovery?', answer: 'Grade I-II: 1-3 weeks. Grade III (non-operative): 6-12 weeks. Post-surgical: 3-4 months before return to full activity. High-level athletes may require 4-6 months before return to competition.' },
    { question: 'Will I have a permanent bump?', answer: 'Grade III-VI injuries may leave a visible bump where the clavicle is elevated. This is cosmetic and does not affect function in most cases. Surgery can reduce the bump but cannot eliminate it entirely.' },
    { question: 'Can I still lift weights?', answer: 'Yes, most patients return to full weightlifting after proper rehabilitation. Initial exercises should avoid heavy pressing and be gradually progressed. Cross-body movements and heavy bench press may cause discomfort initially.' }
  ];

  return (
    <div className="min-h-screen" data-testid="ac-joint-page">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Trauma & Fractures
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">AC Joint Injury Treatment in Hyderabad</h1>
            <p className="text-xl text-gray-600">Shoulder separation & reconstruction by Dr. Deepthi Nandan Reddy at Apollo Hospitals</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding AC Joint Injuries</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>The AC joint connects the clavicle to the scapula at the top of the shoulder. Injuries typically occur from direct falls onto the shoulder (common in contact sports, cycling accidents) or falls onto an outstretched hand.</p>
              <p><strong>Anatomy:</strong> Two sets of ligaments stabilize the AC joint: the AC ligaments (horizontal stability) and the coracoclavicular (CC) ligaments (vertical stability). The severity of injury depends on which ligaments are torn.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Rockwood Classification</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle className="text-lg">Grade I</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600">
                  <p>• AC ligament sprain</p>
                  <p>• CC ligaments intact</p>
                  <p>• No displacement</p>
                  <p className="font-semibold text-green-700 mt-2">Non-operative</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle className="text-lg">Grade II</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600">
                  <p>• AC ligament torn</p>
                  <p>• CC ligaments sprained</p>
                  <p>• Slight elevation</p>
                  <p className="font-semibold text-green-700 mt-2">Non-operative</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-yellow-500">
                <CardHeader><CardTitle className="text-lg">Grade III</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600">
                  <p>• AC + CC ligaments torn</p>
                  <p>• 25-100% displacement</p>
                  <p>• Visible bump</p>
                  <p className="font-semibold text-yellow-700 mt-2">Controversial</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader><CardTitle className="text-lg">Grade IV</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600">
                  <p>• Posterior displacement</p>
                  <p>• Clavicle into trapezius</p>
                  <p>• All ligaments torn</p>
                  <p className="font-semibold text-orange-700 mt-2">Surgery recommended</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardHeader><CardTitle className="text-lg">Grade V</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600">
                  <p>• Severe displacement</p>
                  <p>• 100-300% CC distance</p>
                  <p>• Deltotrapezial fascia torn</p>
                  <p className="font-semibold text-red-700 mt-2">Surgery required</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardHeader><CardTitle className="text-lg">Grade VI</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600">
                  <p>• Inferior displacement</p>
                  <p>• Very rare</p>
                  <p>• High-energy injury</p>
                  <p className="font-semibold text-red-700 mt-2">Surgery required</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment Approach</h2>
            </div>
            <div className="space-y-6">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle>Non-Operative (Grade I-II, Selected Grade III)</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Sling for 1-2 weeks</p>
                  <p>• Ice and anti-inflammatory medications</p>
                  <p>• Early range of motion as tolerated</p>
                  <p>• Gradual return to activity (2-6 weeks)</p>
                  <p className="bg-green-50 p-3 rounded mt-3 text-sm">Grade III: Trial of conservative treatment is reasonable. Surgery can be performed later if symptoms persist.</p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader><CardTitle>Surgical Options (Grade IV-VI, Failed Conservative)</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p><strong>Acute (&lt;3 weeks):</strong> CC ligament repair/reconstruction, hook plate, or TightRope/suture button techniques</p>
                  <p><strong>Chronic:</strong> CC ligament reconstruction with tendon graft, modified Weaver-Dunn procedure</p>
                  <p><strong>Arthritis:</strong> Distal clavicle excision (Mumford procedure)</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-gray-500" />
              <h3 className="text-xl font-bold text-gray-700">Scientific References</h3>
            </div>
            <div className="bg-white p-6 rounded-lg text-sm text-gray-600 space-y-2">
              <p>1. Rockwood CA. Injuries to the acromioclavicular joint. In: Fractures in Adults. 1984</p>
              <p>2. Beitzel K, et al. Current concepts in the treatment of AC joint dislocations. Arthroscopy. 2013</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default ACJointInjury;
